import React, { useState, useEffect, useContext, useCallback } from "react";
import { UserContext } from "../../../contexts/user.context";
import { Get, handleError } from "../../../utils/fetch.util";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import LoadingData from "../../../components/loadingData/loadingData.component";
import {
  FiUser,
  FiTruck,
  FiCheck,
  FiXCircle,
  FiClock,
} from "react-icons/fi";
import Select from "react-select";
import Image from "../../../components/Image/Image.component";
import { debounce } from "lodash";

interface VisitorRequestLog {
  id: number;
  client_id: number;
  visitor_request_id: number;
  status: "pending" | "approved" | "rejected";
  reason: string;
  log_by_id: number;
  createdAt: string;
  visitor_request: {
    id: number;
    name: string;
    nationalId: string;
    reason: string;
    allowVehicle: boolean;
  };
  log_by: {
    asdc_client_user_id: number;
    asdc_client_user_name: string;
    profile_pic: string;
  };
}

interface ApiResponse {
  status: string;
  data: {
    count: number;
    visitorRequestLogs: VisitorRequestLog[];
  };
}

export default function VisitorRequestsHistory() {
  const { t } = useTranslation();
  const { getValidToken, userData } = useContext(UserContext);

  // State management
  const [visitorRequestLogs, setVisitorRequestLogs] = useState<VisitorRequestLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [activeTab, setActiveTab] = useState<"visitor" | "vehicle">("visitor");
  const [approvalStatus, setApprovalStatus] = useState<string[]>([]);

  const sites = userData[0]?.user_sites_relationShip?.map((site: any) => site?.site) || [];
  const sitesOptions = sites.map((site: any) => ({
    value: site.site_id,
    label: site.site_name,
  }));

  const [siteId, setSiteId] = useState<{ value: number; label: string } | null>(null);

  // Fetch visitor request logs
  const fetchVisitorRequestLogs = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        pageItemsCount: pageSize.toString(),
        vehicle_only: activeTab === "vehicle" ? "true" : "false",
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(siteId?.value && { site_id: siteId.value.toString() }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(approvalStatus.length > 0 && { approval_status: JSON.stringify(approvalStatus) }),
      } as any);

      const response = await Get(
        `visitor-requests/get-visitor-request-logs?${queryParams}`,
        getValidToken()
      );

      if (response.error || !response.data) {
        handleError(response);
        return;
      }

      const apiResponse: ApiResponse = response;
      setVisitorRequestLogs(apiResponse.data.visitorRequestLogs);
      setTotalCount(apiResponse.data.count);
    } catch (error) {
      console.error("Error fetching visitor request logs:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce search term changes
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setDebouncedSearchTerm(term);
    }, 500),
    []
  );

  // Update debounced search term when search term changes
  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, siteId, startDate, endDate, activeTab, approvalStatus]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchVisitorRequestLogs();
  }, [currentPage, debouncedSearchTerm, siteId, startDate, endDate, activeTab, approvalStatus]);

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusStyles = () => {
      switch (status) {
        case "approved":
          return "bg-green-100 text-green-800";
        case "rejected":
          return "bg-red-100 text-red-800";
        case "pending":
        default:
          return "bg-yellow-100 text-yellow-800";
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case "approved":
          return <FiCheck className="w-4 h-4 mr-1" />;
        case "rejected":
          return <FiXCircle className="w-4 h-4 mr-1" />;
        case "pending":
        default:
          return <FiClock className="w-4 h-4 mr-1" />;
      }
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyles()}`}
      >
        {getStatusIcon()}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Log card component
  const LogCard = ({ log }: { log: VisitorRequestLog }) => {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-100">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center">
            {log.log_by.profile_pic ? (
              <Image
                src={log.log_by.profile_pic}
                alt={log.log_by.asdc_client_user_name}
                className="w-10 h-10 rounded-full mr-3 object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                <FiUser className="w-6 h-6 text-gray-500" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-medium text-blue-600">
                {log.log_by.asdc_client_user_name}
              </h3>
              <StatusBadge status={log.status} />
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">
              {format(new Date(log.createdAt), "MM/dd/yyyy - hh:mm a")}
            </p>
          </div>
        </div>

        <div className="mt-3 text-gray-700">
          <p>
            <span className="font-medium">
              {log.log_by.asdc_client_user_name} {getActionText(log.status)}{": "}
              {log.visitor_request.name} {activeTab === "visitor" ? "Visitor" : "Vehicle"} Request
            </span>
          </p>
          {log.reason && (
            <p className="mt-1">
              <span className="font-medium">Reason:</span> {log.reason}
            </p>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600">
          <p>
            <span className="font-medium">ID:</span> {log.visitor_request_id}
          </p>
          <p>
            <span className="font-medium">Visit Reason:</span> {log.visitor_request.reason}
          </p>
        </div>
      </div>
    );
  };

  // Helper function to get action text based on status
  const getActionText = (status: string) => {
    switch (status) {
      case "approved":
        return "approved";
      case "rejected":
        return "rejected";
      case "pending":
      default:
        return "created";
    }
  };

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center mt-6 mb-4">
        <nav className="flex items-center space-x-1">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50"
          >
            Previous
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => setCurrentPage(1)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === 1
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                1
              </button>
              {startPage > 2 && (
                <span className="px-2 py-1 text-gray-500">...</span>
              )}
            </>
          )}

          {pageNumbers.map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded-md ${
                currentPage === page
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {page}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="px-2 py-1 text-gray-500">...</span>
              )}
              <button
                onClick={() => setCurrentPage(totalPages)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === totalPages
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </nav>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">Visitor Requests History</h1>

      {/* Tabs */}
      <div className="flex mb-4 border-b">
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "visitor"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("visitor")}
        >
          <FiUser className="inline mr-2" />
          Visitor
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "vehicle"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("vehicle")}
        >
          <FiTruck className="inline mr-2" />
          Vehicle
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            id="search"
            placeholder="Search by visitor name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="site" className="block text-sm font-medium text-gray-700 mb-1">
            Select a Site
          </label>
          <Select
            id="site"
            options={sitesOptions}
            value={siteId}
            onChange={(option) => setSiteId(option)}
            isClearable
            placeholder="Select a site"
            className="basic-single"
            classNamePrefix="select"
          />
        </div>

        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status Filter
          </label>
          <div className="flex flex-wrap gap-2">
            {["pending", "approved", "rejected"].map((status) => (
              <label key={status} className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={approvalStatus.includes(status)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setApprovalStatus([...approvalStatus, status]);
                    } else {
                      setApprovalStatus(approvalStatus.filter((s) => s !== status));
                    }
                  }}
                  className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                />
                <span className="ml-2 mr-3">
                  <StatusBadge status={status} />
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingData />
      ) : (
        <>
          {visitorRequestLogs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No visitor request logs found</p>
            </div>
          ) : (
            <div className="mb-6 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visitorRequestLogs.map((log) => (
                <LogCard key={log.id} log={log} />
              ))}
            </div>
          )}
          <Pagination />
        </>
      )}
    </div>
  );
}

